'use client';
import './map.scss';

/* Map related library */
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer.js';
/* Design library */
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import StraightenIcon from '@mui/icons-material/Straighten';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';

import Collection from '@arcgis/core/core/Collection';
import SnappingOptions from '@arcgis/core/views/interactive/snapping/SnappingOptions';
import FeatureSnappingLayerSource from '@arcgis/core/views/interactive/snapping/FeatureSnappingLayerSource';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
/* React library */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

/* Constants */
import {
  getAreaLabel,
  getIntersectionAreaLabel,
  getIntersectionAreaLabelHardCoded,
} from '@/helper/getArea';

/* Context */
import { MapContext } from '../../context/MapContext/MapProvider';

/* Components */
import SavedAreas from './savedAreas/savedAreas';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEvent } from '@/services/eventEnums';

import { useSpinnerContext } from '@/context/SpinnerContext';
import customToaster from '../toast/CustomToaster';

import { useTranslation } from 'react-i18next';
import { useShoppingCart } from '@/context/ShoppingCartContext';
import posthog from 'posthog-js';
import {
  calculateArea,
  formatAreaUptoDecimalPoint,
  formatAreaUptoOneDecimalPoint,
} from '@/lib/utils';
import { useAuthenticationContext } from '@/context/authenticationProvider';
import LayersPopover from './components/LayersButtonPopover/LayersPopover';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import { getSpexiOrderableCenterGeoJson } from '@/network/serverApis/orderNew';

import { DroneLegendDialog } from './components/DroneLegendDialog';
import AssuredTaskingLegend from '../sider/orderNew/taskingFormComponents/assuredTaskingLegend';
import { saveAreaLocation_rh } from '@/network/routeHandlerService/savedAreas';
import K from '@/constants/constants';

export const MapBox = () => {
  // Map Context
  const {
    view,
    sketchVM,
    zoom,
    basemapToggle,
    previewLayer,
    intersectLayer,
    initializeMap,
    sarSelectedRef,
    drawRectangleHandlerRef,
    startMapProcess1,
    measurementLayerRef,
    polylineLabelRef,
    showSpexiCoverageNavigateButton,
    setShowSpexiCoverageNavigateButton,
    map,
    polygonMode,
    setPolygonMode,
  } = useContext(MapContext);

  // Spinner events
  const { startLoaderEvent, stopLoaderEvent } = useSpinnerContext();
  const { userData } = useAuthenticationContext();

  //translations
  const { t } = useTranslation('orderImagery');

  // on Map Elements ref
  const mapRef = useRef();
  const areaRef = useRef<HTMLDivElement>();
  const rectangleButtonRef = useRef<HTMLButtonElement>(null);
  const clearPolygonButtonRef = useRef<HTMLButtonElement>(null);
  const measurementToolButtonRef = useRef<HTMLButtonElement>(null);

  const polygonButtonRef = useRef<HTMLButtonElement>(null);
  const pointButtonRef = useRef<HTMLButtonElement>(null);
  const [orderNewTab, setOrderNewTab] = useState(false);

  const {
    orderNewDataType,
    orderNewIsGuaranteedCapture,
    tabValue,
    openPreviewInfoPopup,
    setAssuredTaskingDialog,
    assuredTaskingDialog,
    isNew,
    taskingMapLegends,
    aoiHeightWidth,
    setAoiHeightWidth,
  } = useShoppingCart();

  // Auth0 User
  // const { user } = useUser();

  // Saved Area refs and states
  const saveRefData = useRef<any>(null);

  const [locationArea, setLocationArea] = useState<{
    items: any[];
    next: string | null;
  }>({
    items: [],
    next: null,
  });
  const [saveBtnDisable, setSaveBtnDisable] = useState<boolean>(true);
  const [monitorAoiBtnDisable, setMonitorAoiBtnDisable] = useState<boolean>(true);

  // Savd area dialog refs and states
  const [openDialog, setOpenDialog] = useState(false);
  // custom event defined

  const isClearButtonEnabled = posthog.isFeatureEnabled('enable_clear_polygon_button');
  //posthog feature flags
  const mapLayersGeojson = posthog.getFeatureFlag('map_layers_skeleton_geojson');

  useEffect(() => {
    if (initializeMap) {
      initializeMap();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!mapRef.current || !areaRef.current) return;

    if (!view || !sketchVM) return;

    view.container = mapRef.current;

    const customEvent = sketchVM.on('update', (event: any) => {
      if (
        event.state === 'start' ||
        event.toolEventInfo?.type === 'reshape-stop' ||
        //this is the event for fetching the coordinates for save area
        event.toolEventInfo?.type === 'move-stop'
      ) {
        if (event?.graphics?.[0]?.geometry?.type === 'polyline') {
          return;
        }
        const coordinates: any = event.graphics[0].geometry.get('rings');

        if (clearPolygonButtonRef.current) {
          // disable the clear polygon if there is no polygon on the map
          clearPolygonButtonRef.current.disabled =
            sketchVM.layer.graphics.length > 0 ? false : true;
          clearPolygonButtonRef.current.style.opacity =
            sketchVM.layer.graphics.length > 0 ? '1' : '0.5';
        }

        let area = geometryEngine.geodesicArea(
          new Polygon({ rings: coordinates, spatialReference: { wkid: 102100 } }),
          'square-kilometers'
        );

        if (event.graphics[0].attributes?.circle) area = event.graphics[0].attributes?.area;

        const formattedArea = formatAreaUptoOneDecimalPoint(area);

        saveRefData.current = {
          coordinates: webMercatorUtils
            .webMercatorToGeographic(event.graphics[0].geometry)
            .get('rings'),
          area: formattedArea,
        };

        //dont run if the event is not active
        if (event.state !== 'active') {
          //if it is selected from save disable the button as it is already saved or selected from monitor aoi
          // Disable saved area for sarSelected feature
          if (event.graphics[0].attributes?.disable || sarSelectedRef.current) {
            if (event.graphics[0].attributes?.monitorAoi) {
              setMonitorAoiBtnDisable(true);
              setSaveBtnDisable(false);
              event.graphics[0].setAttribute('disable', false);
              event.graphics[0].setAttribute('monitorAoi', false);
            }

            if (event.graphics[0].attributes?.savedArea) {
              setSaveBtnDisable(true);
              setMonitorAoiBtnDisable(false);
              event.graphics[0].setAttribute('disable', false);
              event.graphics[0].setAttribute('savedArea', false);
            }
          } else {
            setSaveBtnDisable(false);
            setMonitorAoiBtnDisable(false);
          }
        }

        //getting the ref for area label
        let childNode = areaRef.current?.children[1];

        //this is for avoiding errors
        if (!childNode) return;

        childNode.children[0].textContent = orderNewTab
          ? getIntersectionAreaLabel(
              formattedArea,
              t,
              orderNewDataType,
              tabValue,
              orderNewIsGuaranteedCapture
            )
          : getAreaLabel(formattedArea, t);
      }

      //this is the event for showing the save area location and disabling the saved areas button
      if (event.state === 'showSaveArea') {
        setSaveBtnDisable(true);
        const coordinates: any = event.graphic.geometry.get('rings');

        let area = geometryEngine.geodesicArea(
          new Polygon({ rings: coordinates, spatialReference: { wkid: 102100 } }),
          'square-kilometers'
        );

        const formattedArea = formatAreaUptoOneDecimalPoint(area);

        saveRefData.current = {
          coordinates: webMercatorUtils
            .webMercatorToGeographic(event.graphic.geometry)
            .get('rings'),
          area: formattedArea,
        };

        // setSaveBtnDisable(false);

        //getting the ref for area label
        let childNode = areaRef.current?.children[1];

        //this is for avoiding errors
        if (!childNode) return;

        childNode.children[0].textContent = orderNewTab
          ? getIntersectionAreaLabel(
              formattedArea,
              t,
              orderNewDataType,
              tabValue,
              orderNewIsGuaranteedCapture
            )
          : getAreaLabel(formattedArea, t);
      }

      if (event.state === 'showIntersectArea') {
        //getting the ref for intersection label
        let childNode = areaRef.current?.children[2];

        if (!childNode || childNode.id == 'clear') return;

        childNode.children[0].textContent = getIntersectionAreaLabelHardCoded(event?.area, t);
      }

      if (event.state === 'clearIntersectArea') {
        //getting the ref for intersection label
        let childNode = areaRef.current?.children[2];

        if (!childNode || childNode.id == 'clear') return;

        childNode.children[0].textContent = getIntersectionAreaLabel(
          0,
          t,
          orderNewDataType,
          tabValue,
          orderNewIsGuaranteedCapture
        );
      }

      if (event.state === 'staticRectangleMode') {
        setPolygonMode(K.POLYGON_MODE.STATIC_RECTANGLE);
      }

      if (event.state === 'polygonMode') {
        setPolygonMode(K.POLYGON_MODE.POLYGON);
      }

      if (event.state === 'circleMode') {
        setPolygonMode(K.POLYGON_MODE.CIRCLE);
      }

      if (event.state === 'pointMode') {
        setPolygonMode(K.POLYGON_MODE.POINT);
      }

      if (event.state === 'orderNew') {
        if (event.show) {
          setOrderNewTab(event.show);
          //getting the ref for area label
          let childNode = areaRef.current?.children[1];

          //this is for avoiding errors
          if (!childNode) return;

          childNode.children[0].textContent = getIntersectionAreaLabel(
            event?.area,
            t,
            orderNewDataType,
            tabValue,
            orderNewIsGuaranteedCapture
          );
        } else {
          setOrderNewTab(event.show);
          //getting the ref for area label
          let childNode = areaRef.current?.children[1];

          //this is for avoiding errors
          if (!childNode) return;

          childNode.children[0].textContent = getAreaLabel(event?.area, t);
        }
      }
    });

    const toolHandle = reactiveUtils.watch(
      () => sketchVM.activeTool,
      tool => {
        if (
          !rectangleButtonRef.current ||
          !polygonButtonRef.current ||
          !measurementToolButtonRef.current
        )
          return;

        // if any of the button is active then highlight it
        if (tool === 'rectangle') {
          rectangleButtonRef.current.style.backgroundColor = '#496178';
        } else if (tool === 'polygon') {
          polygonButtonRef.current.style.backgroundColor = '#496178';
        } else if (tool === 'polyline') {
          measurementToolButtonRef.current.style.backgroundColor = '#496178';
        } else {
          rectangleButtonRef.current.style.backgroundColor = '';
          polygonButtonRef.current.style.backgroundColor = '';
          measurementToolButtonRef.current.style.backgroundColor = '';
        }
      }
    );

    //limiting the map view to not go out of the screen
    view.when(() => {
      const worldWidth = 20037508.34;

      let aspectRatio = 0.9;

      const extent = {
        type: 'extent',
        xmin: -worldWidth,
        xmax: worldWidth,
        ymin: -worldWidth * aspectRatio,
        ymax: worldWidth * aspectRatio,
        spatialReference: { wkid: 102100 },
      };

      view.constraints = {
        geometry: extent as any,
      };

      view.constraints.minZoom = 2;
    });

    //adding drone coverage geojson layers
    async function addDroneGeoJsonLayer() {
      //check if layer is already added
      const orderableGeoJsonLayerExists = view.map.layers.find(
        layer => layer.title === 'orderableGeoJsonLayer'
      );

      if (orderableGeoJsonLayerExists) return;

      // eslint-disable-next-line require-use-server
      const orderableCenterGeoJson = await getSpexiOrderableCenterGeoJson();

      if (!orderableCenterGeoJson) return;

      try {
        const blobOrderableCenter = new Blob([JSON.stringify(orderableCenterGeoJson)], {
          type: 'application/json',
        });

        const urlOrderableCenter = URL.createObjectURL(blobOrderableCenter);

        // tile layer includes orderable and non orderable geojson layers
        const spexiTileLayer = new VectorTileLayer({
          title: 'orderableGeoJsonLayer',
          portalItem: {
            id: `${process.env.NEXT_PUBLIC_SPEXI_TILE_LAYER_ITEM_ID}`,
          },
          // minScale: zoomToScale[9],
          visible: false,
        });

        const orderableCenterGeoJsonLayer = new GeoJSONLayer({
          title: 'orderableCenterGeoJsonLayer',
          url: urlOrderableCenter,
          // featureReduction: {
          //   type: 'cluster',
          //   clusterRadius: '100px',
          //   clusterMaxSize: '45px',
          //   clusterMinSize: '45px',
          //   maxScale: zoomToScale[9],
          //   symbol: droneSymbol,
          //   renderer: { type: 'simple', symbol: droneSymbol } as any,
          // },
          renderer: undefined,
          visible: false,
        });

        view.map.layers.add(spexiTileLayer, 2);

        view.map.layers.add(orderableCenterGeoJsonLayer, 2);

        await Promise.all([spexiTileLayer.load(), orderableCenterGeoJsonLayer.load()]);
      } catch (error) {
        console.error('error adding spexi tile layer', error);
        return;
      }
    }

    startMapProcess1(async () => {
      await addDroneGeoJsonLayer();
    });

    return () => {
      toolHandle.remove();
      customEvent.remove();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sketchVM, orderNewTab]);

  async function handleSaveArea(name: string) {
    let error = false;
    if (!userData) {
      customToaster(`${t('loginTosaveArea', 'Please Login to save areas')}`);
      return false;
    }

    setSaveBtnDisable(true);

    //getting the saved area data from saveRefData
    const coordinates = saveRefData.current.coordinates;
    const area = saveRefData.current.area;
    //saving the data by calling post save area API

    const data = coordinates && (await saveAreaLocation_rh(coordinates, area, name));

    if (data?.error) {
      customToaster(
        `${t('location not saved reason', 'location not saved reason')}: ${data.error}`
      );
      return false;
    }

    customToaster(`${t('locationSaved', 'location saved successfully')}`, 'success');
    const newItem = { id: data?.id, name: data?.name, aoiSize: `${calculateArea(coordinates)}km2` };

    setLocationArea({
      items: [newItem, ...locationArea.items],
      next: locationArea.next,
    });
    return true;
  }
  drawRectangleHandlerRef.current = useCallback(() => {
    //removing previous graphics if exist
    sketchVM.layer.removeAll();
    // previewLayer.source.elements.removeAll();
    intersectLayer.removeAll();

    //for clearing the file polygon when clicking on draw rectangle polygon
    MixpanelTracking.getInstance().clearProperty('file');
    //for tracking
    if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true)
      MixpanelTracking.getInstance().track(MixpanelEvent.ORDER_NEW_DRAW_RECTANGLE_CLICK);
    else MixpanelTracking.getInstance().track(MixpanelEvent.DRAW_RECTANGLE_CLICK);

    //getting the ref of area label
    let childNode = areaRef.current?.children[1];
    if (!childNode) return;
    //resetting the area value
    childNode.children[0].textContent = orderNewTab
      ? getIntersectionAreaLabel(0, t, orderNewDataType, tabValue, orderNewIsGuaranteedCapture)
      : getAreaLabel(0, t);
    //clearing the intersection area value
    sketchVM.emit('update', { state: 'clearIntersectArea' });
    sketchVM.create('rectangle');
  }, [
    sketchVM,
    intersectLayer,
    orderNewDataType,
    orderNewIsGuaranteedCapture,
    orderNewTab,
    t,
    tabValue,
  ]);

  return (
    <Box ref={areaRef} className="map-wrapper">
      <Box className="map-view" id="viewDiv" ref={mapRef}></Box>

      {/* ref is used here for manipulating dom dont add ,remove or change the element here */}
      <Chip
        className="total-area"
        label={getAreaLabel(0, t)}
        sx={{
          visibility:
            polygonMode === K.POLYGON_MODE.STATIC_RECTANGLE || polygonMode === K.POLYGON_MODE.POINT
              ? 'hidden'
              : 'visible',
        }}
      />
      {!orderNewTab ? (
        <Chip
          className="total-intersection-area"
          label={getIntersectionAreaLabel(
            0,
            t,
            orderNewDataType,
            tabValue,
            orderNewIsGuaranteedCapture
          )}
        />
      ) : (
        <div id="clear"></div>
      )}

      {process.env.NEXT_PUBLIC_SHOW_SAVED_AREAS === 'true' && (
        <SavedAreas
          previewLayer={previewLayer}
          userData={userData}
          view={view}
          sketchVM={sketchVM}
          locationArea={locationArea}
          setLocationArea={setLocationArea}
          intersectLayer={intersectLayer}
          orderNewTab={orderNewTab}
          areaRef={areaRef}
          saveBtnDisable={saveBtnDisable}
          monitorAoiBtnDisable={monitorAoiBtnDisable}
          handleSaveArea={handleSaveArea}
        />
      )}
      {showSpexiCoverageNavigateButton && (
        <Button
          size="small"
          variant="contained"
          className="btn-save-area"
          sx={{ top: '135px !important' }}
          onClick={async () => {
            const orderAbleSpexiCoverageLayer = map.layers.find(
              (layer: any) => layer.title === 'orderableGeoJsonLayer'
            );
            if (orderAbleSpexiCoverageLayer) {
              if (view) {
                await view.goTo({ target: [0, 0] }, { duration: 1000 });

                view.goTo({ target: orderAbleSpexiCoverageLayer.fullExtent }, { duration: 2000 });
              }

              setShowSpexiCoverageNavigateButton(false);
            }
          }}
        >
          {t('zoomToDrone', 'Show Drone Coverage Extent')}
        </Button>
      )}

      <Stack direction="column" spacing={{ xs: ' 1rem', md: '2rem' }} className="map-btns">
        {mapLayersGeojson && (
          <Stack direction="column" spacing={0.5}>
            <LayersPopover />
          </Stack>
        )}
        <Stack direction="column" spacing={0.5}>
          {polygonMode === K.POLYGON_MODE.POINT && (
            <Tooltip
              title="Draw Point"
              slotProps={{
                popper: {
                  className: 'thm-dark-tooltip',
                },
              }}
              arrow
              placement="left"
            >
              <Button
                aria-label="draw-point-button"
                name="draw-point-button"
                variant="contained"
                ref={pointButtonRef}
                onClick={e => {
                  //removing previous graphics if exist
                  sketchVM.layer.removeAll();
                  intersectLayer.removeAll();

                  //for clearing the file polygon when clicking on draw point
                  MixpanelTracking.getInstance().clearProperty('file');

                  //clearing the intersection area value
                  sketchVM.emit('update', { state: 'clearIntersectArea' });
                  sketchVM.create('point');
                }}
              >
                <i className="icon-location"></i>
              </Button>
            </Tooltip>
          )}
          <Tooltip
            title="Draw Rectangle"
            slotProps={{
              popper: {
                className: 'thm-dark-tooltip',
              },
            }}
            arrow
            placement="left"
          >
            <span>
              <Button
                aria-label="draw-rectangular-polygon-button"
                name="draw-rectangle-polygon-button"
                variant="contained"
                ref={rectangleButtonRef}
                disabled={polygonMode === K.POLYGON_MODE.POINT}
                onClick={e => {
                  drawRectangleHandlerRef.current && drawRectangleHandlerRef.current();
                }}
              >
                <i className="icon-rectangle-polygon"></i>
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            slotProps={{
              popper: {
                className: 'thm-dark-tooltip',
              },
            }}
            title="Draw Polygon"
            arrow
            placement="left"
          >
            <span>
              <Button
                aria-label="draw-polygon-button"
                variant="contained"
                name="draw-line-polygon-button"
                ref={polygonButtonRef}
                disabled={polygonMode === K.POLYGON_MODE.POINT}
                onClick={e => {
                  //removing previous graphics if exist
                  sketchVM.layer.removeAll();
                  // previewLayer.source.elements.removeAll();
                  intersectLayer.removeAll();

                  //for tracking

                  //for clearing the file polygon when clicking on draw line polygon
                  MixpanelTracking.getInstance().clearProperty('file');
                  if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true)
                    MixpanelTracking.getInstance().track(
                      MixpanelEvent.ORDER_NEW_DRAW_POLYGON_CLICK
                    );
                  else MixpanelTracking.getInstance().track(MixpanelEvent.DRAW_POLYGON_CLICK);

                  //getting the ref for area label
                  let childNode = areaRef.current?.children[1];
                  if (!childNode) return;
                  //resetting the area value
                  childNode.children[0].textContent = orderNewTab
                    ? getIntersectionAreaLabel(
                        0,
                        t,
                        orderNewDataType,
                        tabValue,
                        orderNewIsGuaranteedCapture
                      )
                    : getAreaLabel(0, t);
                  //clearing the intersection area value
                  sketchVM.emit('update', { state: 'clearIntersectArea' });
                  sketchVM.create('polygon');
                }}
              >
                <i className="icon-line-polygon"></i>
              </Button>
            </span>
          </Tooltip>

          {isClearButtonEnabled && (
            <Tooltip
              title="Clear Polygon"
              slotProps={{
                popper: {
                  className: 'thm-dark-tooltip',
                },
              }}
              arrow
              placement="left"
            >
              <span>
                <Button
                  aria-label="clear-polygon-button"
                  name="clear-polygon-button"
                  sx={{ opacity: 0.5 }}
                  // variant="contained"
                  ref={clearPolygonButtonRef}
                  onClick={e => {
                    sketchVM.layer.removeAll();
                    // previewLayer.source.elements.removeAll();
                    intersectLayer.removeAll();
                    sketchVM.emit('update', { state: 'clearPolygon' });
                    if (clearPolygonButtonRef.current) {
                      setAoiHeightWidth({ width: null, height: null });
                      clearPolygonButtonRef.current.disabled = true;
                      clearPolygonButtonRef.current.style.opacity = '0.5';
                    }
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="column" spacing={0.5}>
          <Tooltip
            slotProps={{
              popper: {
                className: 'thm-dark-tooltip',
              },
            }}
            title="Change Basemap"
            arrow
            placement="left"
          >
            <Button
              aria-label="change-basemap-button"
              name="change-basemap-button"
              variant="contained"
              onClick={async () => {
                await basemapToggle.toggle();
              }}
            >
              {/* <GridViewIcon /> */}
              <i className="icon-grid-view"></i>
              {/* <i className="icon-map-layers1"></i> */}
            </Button>
          </Tooltip>
        </Stack>
        <Stack direction="column" spacing={0.5} className="map-icon-btns">
          <Tooltip
            slotProps={{
              popper: {
                className: 'thm-dark-tooltip',
              },
            }}
            title="Zoom In"
            arrow
            placement="left"
          >
            <Button
              aria-label="zoom-in-button"
              name="zoom-in-button"
              variant="contained"
              onClick={() => {
                zoom?.zoomIn();
              }}
            >
              <AddOutlinedIcon />
            </Button>
          </Tooltip>
          <Tooltip
            slotProps={{
              popper: {
                className: 'thm-dark-tooltip',
              },
            }}
            title="Zoom Out"
            arrow
            placement="left"
          >
            <Button
              aria-label="zoom-out-button"
              name="zoom-out-button"
              variant="contained"
              onClick={() => {
                zoom?.zoomOut();
              }}
            >
              <RemoveOutlinedIcon />
            </Button>
          </Tooltip>
          <Tooltip
            slotProps={{
              popper: {
                className: 'thm-dark-tooltip',
              },
            }}
            title="Measurement Tool"
            arrow
            placement="left"
          >
            <Button
              aria-label="measurement-tool-button"
              ref={measurementToolButtonRef}
              name="measurement-tool-button"
              variant="contained"
              onClick={() => {
                if (measurementLayerRef.current || polylineLabelRef.current) {
                  if (measurementLayerRef.current) {
                    sketchVM.layer.graphics.remove(measurementLayerRef.current);
                    measurementLayerRef.current = null;
                  }
                  if (polylineLabelRef.current) {
                    sketchVM?.layer?.remove(polylineLabelRef.current);
                    polylineLabelRef.current = null;
                  }
                  sketchVM.snappingOptions.featureSources.removeAll();
                } else {
                  sketchVM.snappingOptions = new SnappingOptions({
                    enabled: true,
                    selfEnabled: true,
                    featureSources: new Collection([
                      new FeatureSnappingLayerSource({
                        layer: sketchVM?.layer as any,
                        enabled: true,
                      }),
                    ]),
                    distance: 1,
                  });
                  sketchVM.create('polyline');
                }
              }}
            >
              <StraightenIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Show Guaranteed Tasking AOI requirements Dialog on Map when someone select Guaranteed option on tasking screen. User can minimize and maximize the dialog  */}
      {tabValue.current === '2' && taskingMapLegends?.assuredTasking && <AssuredTaskingLegend />}

      <Box className="left-corner-map-info-wrapper">
        <Box className="left-corner-map-info-content-wrapper">
          {(aoiHeightWidth?.height || aoiHeightWidth?.width) && (
            <Box className="height-width-info-box">
              <Typography variant="caption" sx={{ lineHeight: '1.2' }}>
                Height: <span>{formatAreaUptoDecimalPoint(aoiHeightWidth.height || 0, 3)} km</span>
              </Typography>
              <Typography variant="caption" sx={{ lineHeight: '1.2' }}>
                Width: <span>{formatAreaUptoDecimalPoint(aoiHeightWidth.width || 0, 3)} km</span>
              </Typography>
            </Box>
          )}
          {/* Show Drone Tasking AOI Dialog on Map when someone select Drone option on tasking screen. User can minimize and maximize the dialog  */}

          {tabValue.current === '2' && taskingMapLegends?.droneTasking && <DroneLegendDialog />}
        </Box>
      </Box>
    </Box>
  );
};
